import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage, useT } from "@/lib/LanguageContext";
import * as crmApi from "../api/crmApi";
import { apiErrorCode, crmErrorMessage } from "../api/crmErrors";
import { CRM_ERRORS, SEARCH_MIN_CHARS, STAGE_KEYS } from "../contract";
import { fmtCount, fmtRelative } from "../format";
import { FOCUS_RING } from "../ui/classes";
import useClientFilters from "../hooks/useClientFilters";
import useCrmClients from "../hooks/useCrmClients";
import useCrmMoney from "../hooks/useCrmMoney";
import useNow from "../hooks/useNow";
import useRole from "../hooks/useRole";
import useTags from "../hooks/useTags";
import { clientHref, crmHref } from "../paths";
import { datedFilename, downloadBlob } from "../../../lib/download";
import CrmPageHeader from "../ui/CrmPageHeader";
import CrmButton from "../ui/CrmButton";
import CrmInput from "../ui/CrmInput";
import CrmCard from "../ui/CrmCard";
import DataTable from "../ui/DataTable";
import useSelection from "../ui/useSelection";
import BulkActionBar from "../ui/BulkActionBar";
import CrmPager from "../ui/CrmPager";
import EmptyState from "../ui/EmptyState";
import CrmLoadFailure from "../ui/CrmLoadFailure";
import StageBadge from "../ui/StageBadge";
import PackBalanceBadge from "../ui/PackBalanceBadge";
import ClientFilterBar from "../clients/ClientFilterBar";
import SavedViewsMenu from "../clients/SavedViewsMenu";
import ImportBanner from "../clients/ImportBanner";
import BulkDialog, { BulkDeleteDialog } from "../clients/BulkDialogs";
import { DeleteClientDialog } from "../detail/PrivacyDialogs";
import AddClientSheet from "../clients/AddClientSheet";
import { CRM } from "../../../constants/testIds/crm";

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Admin → CRM → Clients (spec Screen 2): search, saved views, Import, Export CSV,
 * "+ Add client"; the filter bar; the import banner; the table with per-row and
 * select-all checkboxes, the bulk bar and 50 per page.
 *
 * Delete (owner only, owner decision 2026-10-21): a trash control on each row opens the
 * record's own delete dialog without opening the record, and the bulk bar's "Delete
 * selected" asks for the selected count to be typed. Both are the same server-side delete
 * (`DELETE /crm/clients/{id}`, `POST /crm/clients/bulk-delete`, owner-gated there too).
 *
 * Filters and sort live in the address (useClientFilters). The search box is local and
 * reaches the address 300 ms after the last keystroke; under 3 characters the list is
 * read without `q` (spec "Live after 3 characters").
 */
export default function CrmClients() {
  const t = useT("crm");
  const tc = useT("common");
  const { lang } = useLanguage();
  const money = useCrmMoney();
  const now = useNow(60000);
  const navigate = useNavigate();
  const { can } = useRole();
  const { tags } = useTags();
  const { filters, sort, setFilter, setSort, clear, applyView, apiFilters, query, isFiltered, viewId } = useClientFilters();
  const list = useCrmClients(query);
  const selectionKey = JSON.stringify(query);
  const selection = useSelection(selectionKey);
  const [search, setSearch] = useState(filters.q);
  const [adding, setAdding] = useState(false);
  const [bulk, setBulk] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const owner = can("mergeEraseDelete");

  // The address wins when it changes from outside (a saved view, Clear filters, Back).
  useEffect(() => { setSearch(filters.q); }, [filters.q]);
  useEffect(() => {
    if (search === filters.q) return undefined;
    const id = setTimeout(() => setFilter("q", search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search, filters.q, setFilter]);

  const selected = selection.count(list.total);
  const payload = () => ({ selection: selection.payload(apiFilters), ...(viewId ? { view_id: viewId } : {}) });

  const exportCsv = async (body, stem) => {
    setBusy(true);
    try {
      downloadBlob(await crmApi.exportClients(body), datedFilename(stem, "csv"));
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc, "errors.exportFailed"));
    } finally {
      setBusy(false);
    }
  };

  const runBulk = async (params) => {
    setBusy(true);
    try {
      const { selection: sel } = payload();
      const res = await crmApi.bulkClients({ selection: sel, action: bulk, params });
      const skipped = Array.isArray(res?.skipped) ? res.skipped.length : Number(res?.skipped) || 0;
      toast.success(t("bulk.done", { n: fmtCount(res?.affected ?? selected, lang), skipped: fmtCount(skipped, lang) }));
      setBulk(null);
      selection.clear();
      list.reload();
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setBusy(false);
    }
  };

  const runBulkDelete = async () => {
    try {
      const res = await crmApi.bulkDeleteClients({ selection: payload().selection, expected_count: selected });
      toast.success(t("clientDelete.bulkDone", { n: fmtCount(res?.deleted ?? selected, lang) }));
      setBulkDeleting(false);
      selection.clear();
      list.reload();
    } catch (e) {
      // The selection no longer resolves to the confirmed count: refresh what the owner
      // sees (a filter selection's count follows the new total), then let the dialog say so.
      if (apiErrorCode(e) === CRM_ERRORS.deleteCountMismatch) list.reload();
      throw e;
    }
  };

  const columns = useMemo(() => [
    { key: "name", header: t("clients.colName"), sortKey: "name", render: (r) => <span className="font-medium">{r.display_name || r.primary_email}</span> },
    { key: "email", header: t("clients.colEmail"), sortKey: "email", className: "max-w-[14rem]", render: (r) => <span className="block truncate" title={r.primary_email}>{r.primary_email}</span> },
    { key: "stage", header: t("clients.colStage"), sortKey: "stage", render: (r) => (STAGE_KEYS[r.stage] ? <StageBadge stage={r.stage} mode={r.stage_mode} short /> : "—") },
    {
      key: "tags", header: t("clients.colTags"), sortKey: "tags",
      render: (r) => (r.tags?.length ? <span className="block max-w-[12rem] truncate" title={r.tags.join(", ")}>{r.tags.join(", ")}</span> : "—"),
    },
    { key: "pack", header: t("clients.colPack"), sortKey: "pack_balance", render: (r) => (r.pack ? <PackBalanceBadge pack={r.pack} /> : "—") },
    { key: "activity", header: t("clients.colActivity"), sortKey: "last_activity_at", render: (r) => (r.last_activity_at ? fmtRelative(r.last_activity_at, now, lang) : "—") },
    { key: "spend", header: t("clients.colSpend"), sortKey: "lifetime_spend_cents", render: (r) => money(r.lifetime_spend_cents, r.currency) },
    ...(owner ? [{
      key: "actions", header: <span className="sr-only">{t("clientDelete.colActions")}</span>, className: "w-10 text-right",
      render: (r) => (
        // The row itself opens the record; this control must not.
        <button type="button" aria-label={t("clientDelete.rowAction", { name: r.display_name || r.primary_email })}
          onClick={(e) => { e.stopPropagation(); setDeleting(r); }} data-testid={CRM.clientsRowDelete(r.id)}
          className={`rounded-full p-1.5 text-eh-muted hover:bg-eh-cream hover:text-eh-ink ${FOCUS_RING}`}>
          <Trash2 size={16} aria-hidden="true" />
        </button>
      ),
    }] : []),
  ], [t, now, lang, money, owner]);

  const bulkActions = [
    { key: "add_tag", label: t("bulk.addTag"), onClick: () => setBulk("add_tag"), testId: CRM.clientsBulkAction("add_tag") },
    { key: "remove_tag", label: t("bulk.removeTag"), onClick: () => setBulk("remove_tag"), testId: CRM.clientsBulkAction("remove_tag") },
    { key: "set_stage", label: t("bulk.changeStage"), onClick: () => setBulk("set_stage"), testId: CRM.clientsBulkAction("set_stage") },
    ...(can("bulkSequence") ? [{ key: "add_to_sequence", label: t("bulk.addToSequence"), onClick: () => setBulk("add_to_sequence"), testId: CRM.clientsBulkAction("add_to_sequence") }] : []),
    { key: "export", label: t("bulk.exportSelected"), disabled: busy, onClick: () => exportCsv(payload(), "clients-selected"), testId: CRM.clientsBulkAction("export") },
    ...(owner ? [{ key: "delete", label: t("clientDelete.bulkAction"), onClick: () => setBulkDeleting(true), testId: CRM.clientsBulkAction("delete") }] : []),
  ];

  const pageIds = list.rows.map((r) => r.id);
  const offerMatching = selection.mode === "ids" && selection.isPageSelected(pageIds) && list.total > list.rows.length;

  return (
    <div data-testid={CRM.clients} className="space-y-4">
      <CrmPageHeader title={t("clients.title")} className="mb-0" />
      <CrmCard className="space-y-4">
        <label className="relative block">
          <span className="sr-only">{t("clients.search")}</span>
          <Search size={16} aria-hidden="true" className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-eh-muted" />
          <CrmInput type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("clients.search")}
            className="pl-10" aria-describedby="crm-clients-search-hint" data-testid={CRM.clientsSearch} />
          <span id="crm-clients-search-hint" className="sr-only">{t("clients.searchHint", { n: SEARCH_MIN_CHARS })}</span>
        </label>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
          <SavedViewsMenu filters={apiFilters} sort={sort} onApply={applyView} />
          <CrmButton as={Link} variant="link" to={crmHref("/import")} data-testid={CRM.clientsImport}>{t("clients.import")}</CrmButton>
          <CrmButton variant="link" disabled={busy} onClick={() => exportCsv({ selection: { filters: apiFilters }, ...(viewId ? { view_id: viewId } : {}) }, "clients")} data-testid={CRM.clientsExport}>
            {t("clients.export")}
          </CrmButton>
          <CrmButton onClick={() => setAdding(true)} data-testid={CRM.clientsAdd}>{t("clients.add")}</CrmButton>
        </div>
        <ClientFilterBar filters={filters} setFilter={setFilter} clear={clear} tags={tags} isFiltered={isFiltered} />
        <ImportBanner />

        {selection.any && (
          <BulkActionBar
            testId={CRM.clientsBulk}
            countLabel={<span data-testid={CRM.clientsBulkCount}>{t("bulk.selected", { n: fmtCount(selected, lang) })}</span>}
            actions={bulkActions}
            extra={(
              <span className="ml-auto flex flex-wrap items-center gap-3">
                {offerMatching && (
                  <button type="button" onClick={selection.selectAllMatching} data-testid={CRM.clientsSelectMatching} className="underline">
                    {t("bulk.selectAllMatching", { n: fmtCount(list.total, lang) })}
                  </button>
                )}
                <button type="button" onClick={selection.clear} data-testid={CRM.clientsBulkClear} className="text-eh-bg/80 underline">{t("bulk.clear")}</button>
              </span>
            )}
          />
        )}

        {list.loadError && <CrmLoadFailure error={list.loadError} onRetry={list.reload} testId={CRM.clientsError} />}
        {!list.loadError && list.loaded && list.rows.length === 0 && (isFiltered ? (
          <EmptyState testId={CRM.clientsEmptyFiltered} title={t("clients.emptyFiltered")}
            action={<CrmButton variant="outline" onClick={clear}>{t("filters.clear")}</CrmButton>} />
        ) : (
          <EmptyState testId={CRM.clientsEmpty} title={t("clients.empty")} body={t("clients.emptyBody")}
            action={(
              <span className="flex flex-wrap justify-center gap-2">
                <CrmButton as={Link} to={crmHref("/import")}>{t("setup.importTitle")}</CrmButton>
                <CrmButton variant="outline" onClick={() => setAdding(true)}>{t("setup.addTitle")}</CrmButton>
              </span>
            )} />
        ))}
        {list.rows.length > 0 && (
          <>
            <DataTable
              caption={t("clients.title")}
              columns={columns}
              rows={list.rows}
              sort={sort}
              onSort={setSort}
              selection={selection}
              onRowClick={(r) => navigate(clientHref(r.id))}
              ids={{ table: CRM.clientsTable, row: CRM.clientsRow, check: CRM.clientsCheck, checkAll: CRM.clientsCheckAll, sort: CRM.clientsSort }}
            />
            <CrmPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} testId={CRM.clientsPager} />
          </>
        )}
      </CrmCard>

      <AddClientSheet open={adding} onOpenChange={setAdding} onCreated={(c) => (c?.id ? navigate(clientHref(c.id)) : list.reload())} />
      {bulk && <BulkDialog action={bulk} count={fmtCount(selected, lang)} busy={busy} onClose={() => setBulk(null)} onConfirm={runBulk} />}
      {bulkDeleting && (
        <BulkDeleteDialog count={selected} countLabel={fmtCount(selected, lang)} onClose={() => setBulkDeleting(false)} onConfirm={runBulkDelete} />
      )}
      {deleting && (
        <DeleteClientDialog client={deleting} onClose={() => setDeleting(null)}
          onDeleted={() => { selection.forget(deleting.id); setDeleting(null); list.reload(); }} />
      )}
    </div>
  );
}
